import { createContext, useContext, useMemo } from "react";
import type { SupportCategory } from "@/v2-components/pages/support/types/support-data";
import {
  findCategoryById,
  findCategoryPath,
} from "@/v2-components/pages/support/utils/help-center-utils";
import { useLocale } from "@/store/locale-store";
import { useQuery } from "@tanstack/react-query";

const SupportCategoriesContext = createContext<SupportCategory[] | null>(null);

export function SupportDataProvider({
  categories,
  children,
}: {
  categories: SupportCategory[];
  children: React.ReactNode;
}) {
  return (
    <SupportCategoriesContext.Provider value={categories}>
      {children}
    </SupportCategoriesContext.Provider>
  );
}

/** 카테고리 트리 전체 반환 */
export function useSupportCategories(): SupportCategory[] {
  const ctx = useContext(SupportCategoriesContext);
  if (!ctx)
    throw new Error(
      "useSupportCategories must be used within SupportDataProvider",
    );
  return ctx;
}

/** 특정 slug의 카테고리 노드 찾기 (재귀) */
export function useSupportCategory(slug?: string): SupportCategory | undefined {
  const categories = useSupportCategories();
  if (!slug) return undefined;
  return findCategoryById(categories, slug);
}

export type LocalizedPost = {
  id: string;
  category: string;
  title: string;
  content: string;
  snippet?: string;
  hasVideo?: boolean;
  categoryPath?: string[];
};

async function fetchPosts(params: Record<string, string>) {
  const query = new URLSearchParams(params).toString();
  // 끝슬래시 필수 — `trailingSlash: true` 라 없으면 308 으로 한 번 더 왕복한다 (검색 미리보기는 새 검색어마다).
  // docs/pitfalls.md #20 「… slash 없으면 308 왕복」
  const res = await fetch(`/api/support/posts/?${query}`);
  if (!res.ok) throw new Error("Failed to fetch posts");
  return res.json();
}

/** 전체 포스트에서 텍스트 검색 */
export function useSupportSearch(search: string) {
  const { languageCode } = useLocale();
  const categories = useSupportCategories();

  const { data = [], isLoading } = useQuery<LocalizedPost[]>({
    queryKey: ["support-posts", "search", search, languageCode],
    queryFn: () => fetchPosts({ search, lang: languageCode }),
    enabled: !!search.trim(),
    staleTime: Infinity,
  });

  const enriched = useMemo(() => {
    const pathCache = new Map<string, string[]>();
    return data.map((post) => {
      if (!pathCache.has(post.category)) {
        pathCache.set(
          post.category,
          findCategoryPath(categories, post.category),
        );
      }
      return { ...post, categoryPath: pathCache.get(post.category) };
    });
  }, [data, categories]);

  return { posts: enriched, isLoading };
}
